// Shared TicketLyfe / Sou-Sou session (SOU-2.4).
// Everything is behind REACT_APP_SESSION_COOKIES: with the flag off the site behaves as before.

const parseHosts = value =>
  String(value || '')
    .split(',')
    .map(host => host.trim().toLowerCase())
    .filter(Boolean)

export const isSessionCookiesEnabled = () => process.env.REACT_APP_SESSION_COOKIES === 'true'

export const getAllowedRedirectHosts = () => parseHosts(process.env.REACT_APP_REDIRECT_ALLOWED_HOSTS)

const RELATIVE_BASE = 'http://relative.invalid'

const hasScheme = value => /^[a-z][a-z0-9+.-]*:/i.test(value)
// eslint-disable-next-line no-control-regex
const hasForbiddenChars = value => /[\\\s\u0000-\u001f\u007f-\u009f]/.test(value)
const hasEncodedForbiddenChars = value => /%(?:[01][0-9a-f]|5c|7f)/i.test(value)

// Returns the normalized URL when `value` is an absolute http(s) URL to an allow-listed host, else null.
// Exact host match (with port); https only, http only for localhost; no userinfo.
export const getAllowedAbsoluteUrl = (value, allowedHosts = getAllowedRedirectHosts()) => {
  if (typeof value !== 'string' || hasForbiddenChars(value)) return null
  let url
  try {
    url = new URL(value)
  } catch (e) {
    return null
  }
  if (url.username || url.password) return null
  const isLocalhost = url.hostname === 'localhost'
  if (url.protocol !== 'https:' && !(url.protocol === 'http:' && isLocalhost)) return null
  if (!allowedHosts.includes(url.host.toLowerCase())) return null
  return url.href
}

// A `returnTo` URL (Sou-Sou asks TL to send the user back after adding a card): absolute, allow-listed, nothing else.
// Same rules as getAllowedAbsoluteUrl plus the percent-encoded backslash/control-character check; null when unsafe.
export const getSafeReturnUrl = (value, allowedHosts) => {
  if (typeof value !== 'string' || !value || hasEncodedForbiddenChars(value)) return null
  return getAllowedAbsoluteUrl(value, allowedHosts)
}

// Decides where to go after a sign-in. Returns { external: true, url } for an allow-listed absolute URL,
// otherwise { external: false, path } for react-router. With the flag off the target is passed through
// untouched, exactly as the site did before.
export const resolveRedirect = (target, fallback = '/home', { enabled = isSessionCookiesEnabled(), allowedHosts } = {}) => {
  // flag off: untouched, no fallback (callers such as the login modals pass no redirectTo at all)
  if (!enabled) return { external: false, path: target }
  if (typeof target !== 'string' || !target) return { external: false, path: fallback }
  // Browsers strip tab/newline/CR inside URLs and treat a backslash like a slash, so any control character,
  // whitespace (incl. NBSP, U+2028/2029, NEL) or backslash, raw or percent-encoded, makes the target invalid.
  if (hasForbiddenChars(target) || hasEncodedForbiddenChars(target)) return { external: false, path: fallback }
  if (target.startsWith('//') || target.startsWith('\\') || hasScheme(target)) {
    const url = getAllowedAbsoluteUrl(target, allowedHosts)
    return url ? { external: true, url } : { external: false, path: fallback }
  }
  // a relative path must stay on this origin when resolved like a browser would
  try {
    if (new URL(target, RELATIVE_BASE).origin !== RELATIVE_BASE) return { external: false, path: fallback }
  } catch (e) {
    return { external: false, path: fallback }
  }
  return { external: false, path: target }
}

export const isExternalRedirect = (target, options) => resolveRedirect(target, '/home', options).external

export const goAfterSignIn = (history, target, fallback = '/home') => {
  if (!isSessionCookiesEnabled()) {
    // exactly the old behaviour: history.push(target), even when target is undefined
    history.push(target)
    return
  }
  const result = resolveRedirect(target, fallback)
  if (result.external) {
    window.location.assign(result.url)
  } else {
    history.push(result.path)
  }
}

// CSRF double-submit: the backend sets a readable `<session cookie name>_csrf` cookie.
export const getCsrfToken = () => {
  const name = process.env.REACT_APP_SESSION_COOKIE_NAME
  if (!name || typeof document === 'undefined') return null
  const prefix = `${name}_csrf=`
  const part = document.cookie.split('; ').find(item => item.startsWith(prefix))
  if (!part) return null
  try {
    return decodeURIComponent(part.slice(prefix.length))
  } catch (e) {
    // a malformed cookie (e.g. set by a sibling subdomain) must not break state-changing calls
    return null
  }
}

const STATE_CHANGING = ['POST', 'PUT', 'PATCH', 'DELETE']

// Adds credentials and the CSRF header to axios v2 options (flag on only).
export const applySessionCookies = options => {
  if (!isSessionCookiesEnabled()) return options
  options.withCredentials = true
  if (STATE_CHANGING.includes(String(options.method).toUpperCase())) {
    const token = getCsrfToken()
    if (token) options.headers['X-CSRF-Token'] = token
  }
  return options
}
